import { Heart, Package, ShoppingBag } from "lucide-react";
import { formatCurrency } from "@/lib/order";
import type { PublicProduct } from "@/types/store";
import styles from "./retail.module.css";

type Props = {
  compact?: boolean;
  product: PublicProduct;
  showCart: boolean;
  quantity: number;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onOpen: () => void;
  onAdd: () => void;
  onOpenCart: () => void;
};

export default function RetailProductCard({
  compact = false,
  product,
  showCart,
  quantity,
  isFavorite,
  onToggleFavorite,
  onOpen,
  onAdd,
  onOpenCart,
}: Props) {
  const image = product.primary_image_url || product.images[0]?.image_url;
  const hasPromotion = Boolean(
    product.promotional_price &&
    Number(product.promotional_price) < Number(product.price),
  );
  const unavailable =
    product.availability_status === "sold" ? "Vendido" : "Esgotado";
  return (
    <article
      className={`${styles.productCard} ${compact ? styles.compactCard : ""}`}
      data-product-id={product.id}
    >
      <div className={styles.productMedia}>
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Ver detalhes de ${product.name}`}
          className={styles.imageButton}
        >
          {image ? (
            <img
              src={image}
              alt={product.name}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <Package size={40} strokeWidth={1} aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          className={styles.favorite}
          onClick={onToggleFavorite}
          aria-label={`${isFavorite ? "Remover" : "Adicionar"} ${product.name} ${isFavorite ? "dos" : "aos"} favoritos`}
          aria-pressed={isFavorite}
        >
          <Heart
            size={18}
            fill={isFavorite ? "currentColor" : "none"}
            aria-hidden="true"
          />
        </button>
        {!product.is_available && (
          <span className={styles.productStatus}>{unavailable}</span>
        )}
      </div>
      <div className={styles.productBody}>
        {product.category_name && (
          <p className={styles.productCategory}>{product.category_name}</p>
        )}
        <h3>
          <button type="button" onClick={onOpen}>
            {product.name}
          </button>
        </h3>
        {product.reference_code && (
          <p className={styles.reference}>Ref. {product.reference_code}</p>
        )}
        <div className={styles.price}>
          {hasPromotion && <del>{formatCurrency(product.price)}</del>}
          <strong>{formatCurrency(product.current_price)}</strong>
        </div>
        {showCart ? (
          <button
            type="button"
            className={styles.buyButton}
            disabled={!product.is_available}
            onClick={
              quantity > 0 && !product.has_variations ? onOpenCart : onAdd
            }
          >
            <ShoppingBag size={16} aria-hidden="true" />
            {!product.is_available
              ? unavailable
              : quantity > 0 && !product.has_variations
                ? `No carrinho (${quantity})`
                : product.has_variations || product.option_groups?.length
                  ? "Escolher opções"
                  : "Adicionar"}
          </button>
        ) : (
          <button type="button" className={styles.buyButton} onClick={onOpen}>
            Ver detalhes
          </button>
        )}
      </div>
    </article>
  );
}
